import { Column, Row } from '@cdab/scania/qpr/components/atoms'
import { useDeviation } from '@cdab/scania/qpr/contexts/deviation'
import { useDeviationAutoSave } from '@cdab/scania/qpr/hooks'
import { deviationsController } from '@cdab/scania/qpr/offline/controllers'
import type {
  CreateDeviation,
  Deviation,
  DeviationValidationSchemaDraftOut,
  DeviationValidationSchemaOut
} from '@cdab/scania/qpr/schema'
import { capitalizeFirstLetter } from '@cdab/utils'
import { TdsButton, TdsIcon, TdsToast, TdsToggle } from '@scania/tegel-react'
import { useState } from 'react'
import type { FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import type { DeviationsActionButtonsProps } from './new-deviation-action-buttons.types'

const ActionContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: row;
  align-items: center;
`

export function NewDeviationActionButtons({
  onResetDeviation,
  existingDeviation,
  isReadonly,
  deviation,
  auditId,
  onShowErrorHelpers
}: DeviationsActionButtonsProps) {
  const { t } = useTranslation()
  const { pathname, state } = useLocation()
  const navigate = useNavigate()
  const ctx = useDeviation()

  const [created, setCreated] = useState(!!existingDeviation)

  const [createMore, setCreateMore] = useState(false)
  const goToPreviousPage = () => {
    if (existingDeviation) {
      sessionStorage.setItem('previousDeviationGuid', deviation.clientGuid)
    }
    const lastIndexPath = pathname.lastIndexOf('/')
    let previousPath: string | undefined = undefined
    if (lastIndexPath !== -1) {
      previousPath = pathname.substring(0, lastIndexPath)
    }

    if (state && 'previousPath' in state) {
      navigate(state.previousPath)
    } else if (previousPath) {
      navigate(previousPath)
    }
  }

  const updateCreateDraftDeviation = (
    data: DeviationValidationSchemaDraftOut
  ): Promise<void> => {
    const deviationData: Deviation = data.withActionPlan
      ? {
          actionPlanId: deviation.actionPlanId,
          auditId,
          clientGuid: deviation.clientGuid,
          auditPointNumber: data.auditPointNumber ?? '',
          auditPointId: data.auditPointId > -1 ? data.auditPointId : null,
          deviation: data.deviation ?? '',
          draft: data.draft ?? false,
          id: deviation.id,
          deviationWithoutActions: false,
          approvalDate: data.completed != null ? data.completed : null,
          expirationDate: data.due != null ? data.due : null,
          proposedActions: data.action ?? '',
          responsible: data.assignee ?? ''
        }
      : {
          actionPlanId: deviation.actionPlanId,
          auditId,
          clientGuid: deviation.clientGuid,
          auditPointNumber: data.auditPointNumber ?? '',
          auditPointId: data.auditPointId > -1 ? data.auditPointId : null,
          deviation: data.deviation ?? '',
          draft: data.draft ?? false,
          id: deviation.id,
          deviationWithoutActions: true,
          approvalDate: null,
          expirationDate: null,
          proposedActions: null,
          responsible: null
        }

    if (created) {
      deviationsController.UpdateDeviation(deviationData)
    } else {
      deviationsController.CreateDeviation(deviationData)
      setCreated(true)
    }
    return Promise.resolve()
  }

  const createDeviation = (
    data: DeviationValidationSchemaOut
  ): Promise<void> => {
    cancelAutosave()
    const deviationData: CreateDeviation = data.withActionPlan
      ? {
          auditId,
          clientGuid: deviation.clientGuid,
          auditPointNumber: data.auditPointNumber,
          auditPointId: data.auditPointId,
          deviationWithoutActions: false,
          deviation: data.deviation,
          responsible: data.assignee,
          proposedActions: data.action,
          expirationDate: data.due ?? null,
          approvalDate: data.completed ?? null,
          actionPlanId: -1,
          draft: false
        }
      : {
          auditId,
          clientGuid: deviation.clientGuid,
          auditPointNumber: data.auditPointNumber,
          auditPointId: data.auditPointId,
          deviationWithoutActions: true,
          deviation: data.deviation,
          expirationDate: null,
          proposedActions: null,
          responsible: null,
          approvalDate: null,
          actionPlanId: -1,
          draft: false
        }
    if (created) {
      deviationsController.UpdateDeviation(deviationData)
    } else {
      deviationsController.CreateDeviation(deviationData)
      setCreated(true)
    }

    return Promise.resolve()
  }

  async function saveDeviation(
    deviation: DeviationValidationSchemaOut | FieldValues
  ) {
    try {
      await createDeviation(deviation as DeviationValidationSchemaOut)

      toast(
        <TdsToast
          variant='success'
          header={capitalizeFirstLetter(t('deviation-created'))}
          subheader={capitalizeFirstLetter(t('deviation-created-success'))}
        />
      )

      if (!createMore) {
        goToPreviousPage()
      } else {
        onResetDeviation()
      }
    } catch (error) {
      toast(
        <TdsToast
          variant='error'
          header={capitalizeFirstLetter(t('deviation-not-created'))}
          subheader={capitalizeFirstLetter(t('deviation-not-created-error'))}
        />
      )
    }
  }

  const onDeleteDeviation = () => {
    if (
      window.confirm('Are you sure you want to delete this draft deviation?')
    ) {
      cancelAutosave()
      setTimeout(() => {
        deviationsController.DeleteDeviation(auditId, deviation.clientGuid)
        goToPreviousPage()
        toast(
          <TdsToast
            variant='success'
            header={capitalizeFirstLetter(t('draft-deleted'))}
            subheader={capitalizeFirstLetter(t('draft-deleted-success'))}
          />
        )
      }, 500)
    }
  }

  const { cancelAutosave } = useDeviationAutoSave(
    created,
    deviation,
    updateCreateDraftDeviation
  )

  return (
    <Row className='tds-u-mb2 tds-u-mt2'>
      <Column width={12} lg={7}>
        <TdsButton
          variant='secondary'
          text={capitalizeFirstLetter(t('back', { ns: 'common' }))}
          size='sm'
          onClick={goToPreviousPage}
        />
        <ActionContainer className='tds-u-float-right'>
          <TdsButton
            variant='danger'
            size='sm'
            onClick={() => onDeleteDeviation()}
          >
            <TdsIcon slot='icon' name='trash' />
          </TdsButton>
          {!existingDeviation && (
            <TdsToggle
              size='sm'
              onClick={() => setCreateMore(!createMore)}
              checked={createMore}
            >
              <div slot='label'>
                {capitalizeFirstLetter(t('create-more', { ns: 'common' }))}
              </div>
            </TdsToggle>
          )}
          <TdsButton
            size='sm'
            onClick={() => {
              ctx?.form?.setValue('draft', false)
              ctx?.form?.handleSubmit(saveDeviation, () => {
                onShowErrorHelpers && onShowErrorHelpers()
                ctx?.form?.setValue('draft', true)
              })()
            }}
            text={capitalizeFirstLetter(t('create', { ns: 'common' }))}
            disabled={isReadonly}
          />
        </ActionContainer>
      </Column>
    </Row>
  )
}
